import { useContext } from 'react';
import { Context } from '../Context';
import { isTrackingDisabled } from '../utils/debugging';
import type { StageContainerContent } from '../components/stage-container/StageContainer';
import type { OpenInTabOrLayer } from '../../types';
import { removeSpecialCharacters, setEventAction, setTargetURL } from '../utils/trackingUtils';

export interface Tracking {
  sendReadyEvent(): void;
  registerImpressionTracking(): void;
  sendClickEvent(clickTrackingAttributes: ClickTrackingAttributes): void;
}

interface ClickTrackingAttributes {
  url: string; // URL of the clicked element
  label: string; // 	Link text of the clicked element
  index: number | string; // cta index or 'link' in case of anchor link from subline text
  openInTabOrLayer: OpenInTabOrLayer;
}

export function useTracking(
  ref: React.RefObject<HTMLDivElement>,
  content: StageContainerContent,
  attributes?: {
    version?: string;
    implementer?: number;
    [key: string]: unknown;
  },
): Tracking {
  const { trackingService, loggerService } = useContext(Context);

  if (isTrackingDisabled()) {
    loggerService?.info('TRACKING DISABLED');

    return {
      sendReadyEvent: () => {
        loggerService?.info('TRACKING not executed: sendReadyEvent');
      },
      registerImpressionTracking: () => {
        loggerService?.info('TRACKING not executed: registerImpressionTracking');
      },
      sendClickEvent: (clickTrackingAttributes: ClickTrackingAttributes) => {
        loggerService?.info('TRACKING not executed: sendClickEvent', clickTrackingAttributes);
      },
    };
  }

  const { asset, text, buttons } = content;

  const featureAppName = 'editorial-stage';
  const componentNameWithDashes = featureAppName.replace('fa-', '');
  const componentNameWithSpaces = componentNameWithDashes.replace(/-/g, ' ');
  const defaultComponentAdditions = {
    componentInfo: {
      // not accepted by tracking lib
      // componentID: 'bla',
    },
    attributes: {
      version: __FEATURE_APP_VERSION__,
      ...attributes,
    },
  };

  return {
    sendReadyEvent: () => {
      if (trackingService) {
        const readyEvent = {
          event: {
            attributes: {
              componentName: componentNameWithDashes,
              elementName: '',
              label: '',
              clickID: '',
              pos: '',
              targetURL: '',
              value: '',
            },
            eventInfo: {
              eventAction: 'feature_app_ready',
              eventName: `${componentNameWithSpaces} - feature app ready`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        trackingService.track(readyEvent);
      }
    },
    registerImpressionTracking: () => {
      if (trackingService && ref.current) {
        trackingService.registerImpressionTracking(ref.current, () => {
          return {
            event: {
              attributes: {
                componentName: componentNameWithDashes,
                elementName: '',
                label: '',
                clickID: '',
                pos: asset.assetType,
                targetURL: '',
                value: text.headline || '',
                relatedElements:
                  buttons.map((button, index) => {
                    return {
                      elementName: index === 0 ? 'primary cta' : 'secondary cta',
                      elementValue: `${index + 1}-${button.label}`,
                    };
                  }) || '',
              },
              eventInfo: {
                eventAction: 'impression',
                eventName: `${componentNameWithSpaces} - impression`,
              },
            },
            componentUpdate: defaultComponentAdditions,
          };
        });
      }
    },
    sendClickEvent: (clickTrackingAttributes) => {
      const { openInTabOrLayer, url, label, index } = clickTrackingAttributes;
      if (trackingService) {
        loggerService?.info('Tracking: sendClickEvent', clickTrackingAttributes);
        trackingService.track({
          event: {
            attributes: {
              label, // 	Link text of the clicked element
              targetURL:
                openInTabOrLayer === 'layer'
                  ? `${window.location.href}#one-layer${setTargetURL(url, openInTabOrLayer)}`
                  : setTargetURL(url, openInTabOrLayer),
              relatedElements: [
                {
                  elementName: index === 0 ? 'primary cta' : index === 1 ? 'secondary cta' : 'link',
                  elementValue: `${typeof index === 'number' ? index + 1 : 1}-${label}`,
                },
              ], // related elements contains the ctas used in the component
              componentName: 'editorial-stage',
              elementName: index === 'link' ? 'text link' : 'button',
              clickID: '', // fixed value
              pos: '', // fixed value
              value:
                index === 'link'
                  ? removeSpecialCharacters(text.subline)
                  : removeSpecialCharacters(text.headline),
            },
            eventInfo: {
              eventAction:
                url.includes('#oneLayer=') || openInTabOrLayer === 'layer'
                  ? 'content'
                  : setEventAction(trackingService, openInTabOrLayer, url),
              eventName: `editorial stage - click on ${index === 'link' ? 'text link' : 'cta'}${openInTabOrLayer === 'layer' || url.includes('#oneLayer=') ? ' - layer open' : ''}`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        });
      }
    },
  };
}
